import { requestEventWord, stateWord } from "@lafayette/domain";
import type { StaffRequestDetail, StaffRequestQuestion } from "@lafayette/schemas";
import { Link } from "react-router";

import { citedMessageHref } from "../../conversations/cited-message";
import { evidenceMessageAuthorLabels } from "../../customers/labels";
import { recordLinkFor } from "../../diagnostics/record-links";
import { deepLinks } from "../../navigation/deep-links";
import { useAreaHref, useOffering } from "../../navigation/use-offering";
import { Badge } from "../../ui/badge";
import { cn } from "../../ui/cn";
import { Fact, Facts } from "../../ui/facts";
import { Panel } from "../../ui/panel";
import { StaffOnlyNote } from "../../ui/staff-only-note";
import {
  CustomerReadsNote,
  emptyNoteClass,
  hintClass,
  linkClass,
  listClass,
  SubHeading,
} from "../components/parts";
import type { NoteModel, RequestFacts, TransitionModel } from "../detail-model";
import { formatAge, formatInstant, formatWallTime } from "../format";
import {
  answerChannelLabels,
  assignmentSourceLabels,
  evidenceKindLabels,
  evidenceReferenceLabel,
  fieldTypeLabels,
  internalReasonLabel,
  noteKindLabels,
  questionStatusLabels,
  typeLabel,
  workStatusLabels,
} from "../labels";

// The read-only sections of the request detail. Each staff-only section carries the exact staff-only words (`StaffOnlyNote`). Names of
// staff come from the `actors` map of the detail; a missing name reads "a staff member", never a raw id.

const dash = "—";

const actorName = (actors: Readonly<Record<string, string>>, id: string | null): string =>
  id === null ? "a staff member" : (actors[id] ?? "a staff member");

// --- the request ------------------------------------------------------------------------------------------------------------------

export function RequirementsSection({ facts }: Readonly<{ facts: RequestFacts }>) {
  const when = [
    facts.dateFrom,
    facts.dateTo === null ? null : `to ${facts.dateTo}`,
    formatWallTime(facts.preferredTime, facts.timeZone),
  ]
    .filter((part): part is string => part !== null)
    .join(" ");
  return (
    <Panel id="request-requirements-title" title="The request">
      <Facts>
        <Fact term="Type">{typeLabel(facts.requestType)}</Fact>
        <Fact term="When (wall time and zone)">{when === "" ? dash : when}</Fact>
        <Fact term="Party size">{facts.partySize ?? dash}</Fact>
        <Fact term="Where">{facts.locationText ?? dash}</Fact>
        <Fact term="Special requests">{facts.specialRequests ?? dash}</Fact>
        <Fact term="Indicative budget">{facts.budget ?? dash}</Fact>
        {facts.details.map(([key, value]) => (
          <Fact key={key} term={key}>
            {value}
          </Fact>
        ))}
        <Fact term="Submitted">{formatInstant(facts.submittedAt)}</Fact>
        <Fact term="Version">{facts.version}</Fact>
      </Facts>
    </Panel>
  );
}

export function ContextSection({ detail }: Readonly<{ detail: StaffRequestDetail }>) {
  const { context, customer } = detail;
  const prefs = customer.preferences;
  const areaHref = useAreaHref();
  const customerHref = areaHref("customers", deepLinks.customerMemory(customer.id));
  const conversationHref =
    context.conversationId === null
      ? null
      : areaHref("concierge", deepLinks.conversation(context.conversationId));
  return (
    <Panel
      id="request-context-title"
      title="Customer and context"
      description="So you can act without reading the transcript."
    >
      <Facts>
        <Fact term="Customer">
          {customerHref === null ? (
            <span className="font-serif font-medium">{customer.displayName}</span>
          ) : (
            <Link className={cn(linkClass, "font-serif font-medium")} to={customerHref}>
              {customer.displayName}
            </Link>
          )}
        </Fact>
        <Fact term="Customer profile">
          {[prefs.preferredName, prefs.locale, prefs.homeMarket, prefs.contactChannel]
            .filter((part): part is string => part !== null)
            .join(" · ") || dash}
        </Fact>
        <Fact term="Trip">{context.tripTitle ?? dash}</Fact>
        <Fact term="Itinerary item">{context.itineraryItemTitle ?? dash}</Fact>
        <Fact term="Catalog item">{context.catalogItemName ?? dash}</Fact>
        <Fact term="Conversation">
          {conversationHref === null ? (
            dash
          ) : (
            <Link className={linkClass} to={conversationHref}>
              Open the conversation
            </Link>
          )}
        </Fact>
      </Facts>
    </Panel>
  );
}

export function OwnershipSection({
  detail,
  facts,
  viewerStaffUserId = null,
}: Readonly<{
  detail: StaffRequestDetail;
  facts: RequestFacts;
  /** The signed-in staff member, when known: their own name is followed by "(you)". */
  viewerStaffUserId?: string | null;
}>) {
  const { ownership, actors, attention } = detail;
  const named = (person: { staffUserId: string; displayName: string | null }): string =>
    `${person.displayName ?? "A staff member"}${person.staffUserId === viewerStaffUserId ? " (you)" : ""}`;
  return (
    <Panel id="request-ownership-title" title="Ownership">
      <StaffOnlyNote />
      <Facts>
        <Fact term="Owner">{ownership.owner === null ? "Nobody yet" : named(ownership.owner)}</Fact>
        <Fact term="Designated assignee">
          {ownership.assignee === null ? "Nobody named" : named(ownership.assignee)}
        </Fact>
        <Fact term="How it was assigned">
          {ownership.assignmentSource === null
            ? dash
            : (assignmentSourceLabels[ownership.assignmentSource] ?? ownership.assignmentSource)}
        </Fact>
        <Fact term="Claimed">{formatInstant(ownership.claimedAt)}</Fact>
        <Fact term="Open for">{formatAge(attention.ageSeconds)}</Fact>
        <Fact term="Waiting for the customer since">
          {formatInstant(attention.awaitingCustomerSince)}
        </Fact>
        <Fact term="Work status">{workStatusLabels[facts.workStatus]}</Fact>
        <Fact term="Started from">{facts.source ?? "Not recorded"}</Fact>
      </Facts>
      {ownership.history.length === 0 ? null : (
        <>
          <SubHeading>Ownership history</SubHeading>
          <ol className={`${listClass} gap-2 text-sm`}>
            {[...ownership.history].reverse().map((entry) => (
              <li key={`${entry.requestVersion}-${entry.event}`}>
                {formatInstant(entry.occurredAt)}: {requestEventWord(entry.event)} by{" "}
                {entry.byActorKind === "staff"
                  ? actorName(actors, entry.byStaffUserId)
                  : entry.byActorKind === "system"
                    ? "the routing service"
                    : "the customer"}
                {entry.event === "CLAIMED"
                  ? ""
                  : entry.toStaffUserId === null
                    ? " to the supervisor queue"
                    : ` to ${actorName(actors, entry.toStaffUserId)}`}
                {entry.code === null
                  ? ""
                  : ` (${assignmentSourceLabels[entry.code] ?? entry.code})`}
              </li>
            ))}
          </ol>
        </>
      )}
    </Panel>
  );
}

// --- questions (staff view) -------------------------------------------------------------------------------------------------------

export function QuestionsSection({
  questions,
  actors,
}: Readonly<{
  questions: readonly StaffRequestQuestion[];
  actors: Readonly<Record<string, string>>;
}>) {
  return (
    <Panel
      id="request-questions-title"
      title="Questions to the customer"
      description="The waiting state, the question and the answer. Who asked, and how the answer arrived, are staff only."
    >
      {questions.length === 0 ? (
        <p className={emptyNoteClass}>No question has been asked.</p>
      ) : (
        <ol className={listClass}>
          {questions.map((question) => (
            <li
              key={question.id}
              className="grid gap-3 border-b border-border pb-4 last:border-b-0 last:pb-0"
            >
              <p className="m-0">
                <strong>Question {question.sequence}</strong>{" "}
                <Badge>{questionStatusLabels[question.status] ?? question.status}</Badge>:{" "}
                {question.prompt}
              </p>
              {question.fields.length === 0 ? (
                <p className={hintClass}>Plain text question. The answer is one free-text reply.</p>
              ) : (
                <ul className="m-0 grid list-none gap-1 p-0 text-sm">
                  {question.fields.map((field) => (
                    <li key={field.key}>
                      {field.label} ({fieldTypeLabels[field.type]}
                      {field.required ? ", required" : ", optional"}
                      {field.options === undefined ? "" : `: ${field.options.join(", ")}`})
                    </li>
                  ))}
                </ul>
              )}
              <Facts>
                <Fact term="Asked">{formatInstant(question.askedAt)}</Fact>
                {question.answeredAt === null ? null : (
                  <Fact term="Answered">{formatInstant(question.answeredAt)}</Fact>
                )}
                {question.withdrawnAt === null ? null : (
                  <Fact term="Withdrawn">{formatInstant(question.withdrawnAt)}</Fact>
                )}
                {question.answer === null ? null : (
                  <Fact term="Answer">
                    {Object.entries(question.answer)
                      .map(([key, value]) => {
                        const label =
                          question.fields.find((field) => field.key === key)?.label ?? key;
                        return `${label}: ${String(value)}`;
                      })
                      .join("; ")}
                  </Fact>
                )}
              </Facts>
              <div className="grid gap-2">
                <StaffOnlyNote />
                <Facts>
                  <Fact term="Asked by">{actorName(actors, question.askedByStaffUserId)}</Fact>
                  <Fact term="Answered how">
                    {question.answerChannel === null
                      ? dash
                      : (answerChannelLabels[question.answerChannel] ?? question.answerChannel)}
                  </Fact>
                  {question.recordedReference === null ? null : (
                    <Fact term="Recorded reference">{question.recordedReference}</Fact>
                  )}
                  {question.sourceMessageRef === null ? null : (
                    <Fact term="Chat message">
                      <SourceMessage question={question} />
                    </Fact>
                  )}
                </Facts>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/**
 * The chat message that answered a question, in words (CHT-1984): "A message from the customer, sent 12 Oct 2026, 09:30 UTC" and a link to the
 * conversation. The raw id of the message is never shown, and neither is its text: the text stays in the conversation, where the access rule
 * and the join apply. A message that Postgres does not know for this customer reads "A chat message", with no link.
 */
function SourceMessage({ question }: Readonly<{ question: StaffRequestQuestion }>) {
  const offering = useOffering();
  const message = question.sourceMessage;
  if (message === null) return <>A chat message</>;
  const edited = message.state === "EDITED" ? ", edited later" : "";
  const removed = message.state === "DELETED" ? ", since removed" : "";
  // The link grants nothing: the conversation page checks the staff member's access again. It is offered only to a role that has the
  // Concierge area, so it never ends in a refusal.
  const conversationHref = recordLinkFor("conversation", message.conversationId, offering).href;
  // The link also names the message, so the conversation opens at it.
  const conversation =
    conversationHref === null || question.sourceMessageRef === null
      ? conversationHref
      : citedMessageHref(conversationHref, question.sourceMessageRef);
  return (
    <span className="grid gap-1">
      <span>
        {`A message from ${evidenceMessageAuthorLabels[message.authorKind]}, sent ${formatInstant(message.sentAt)}${edited}${removed}`}
      </span>
      {conversation === null ? null : (
        <Link className={linkClass} to={conversation}>
          Open message in the conversation
        </Link>
      )}
    </span>
  );
}

// --- internal notes ---------------------------------------------------------------------------------------------------------------

export function NotesSection({
  notes,
  actors,
  action,
}: Readonly<{
  notes: readonly NoteModel[];
  actors: Readonly<Record<string, string>>;
  action: React.ReactNode;
}>) {
  return (
    <Panel id="request-notes-title" title="Internal notes">
      <StaffOnlyNote />
      {action}
      {notes.length === 0 ? (
        <p className={emptyNoteClass}>No internal notes yet.</p>
      ) : (
        <ul className={listClass}>
          {notes.map((note) => (
            <li
              key={note.id}
              className="grid gap-1 rounded-sm border border-border bg-surface-sunken p-3"
            >
              <p className="m-0 text-xs text-muted-foreground">
                {noteKindLabels[note.kind] ?? note.kind} ·{" "}
                {actorName(actors, note.authorStaffUserId)} · {formatInstant(note.createdAt)}
              </p>
              <p className="m-0 break-words">{note.body}</p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// --- history ----------------------------------------------------------------------------------------------------------------------

export function TimelineSection({
  transitions,
  actors,
}: Readonly<{
  transitions: readonly TransitionModel[];
  actors: Readonly<Record<string, string>>;
}>) {
  return (
    <Panel id="request-timeline-title" title="History">
      <StaffOnlyNote>The reasons below are written by staff.</StaffOnlyNote>
      <ol className={listClass}>
        {transitions.map((row) => (
          <li
            key={row.key}
            className="grid gap-1 rounded-sm border border-border p-3 [&_p]:m-0 [&_p]:break-words"
          >
            <p>
              <strong>{requestEventWord(row.event)}</strong>
              {row.fromState !== null && row.toState !== null && row.fromState !== row.toState
                ? `: ${stateWord("request", "staffShort", row.fromState)} → ${stateWord("request", "staffShort", row.toState)}`
                : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatInstant(row.occurredAt)} ·{" "}
              {row.actorKind === "staff"
                ? actorName(actors, row.actorId)
                : row.actorKind === "customer"
                  ? "The customer"
                  : "The system"}
              {row.requestVersion === null ? "" : ` · version ${row.requestVersion}`}
            </p>
            {row.internalReason === null ? null : (
              <p>Reason: {internalReasonLabel(row.internalReason)}</p>
            )}
            {row.evidenceKind === null ? null : (
              <p>
                Evidence: {evidenceKindLabels[row.evidenceKind] ?? row.evidenceKind}
                {row.evidenceReference === null
                  ? ""
                  : ` (${evidenceReferenceLabel(row.evidenceReference)})`}
              </p>
            )}
            {row.customerVisibleMessage === null ? null : (
              <CustomerReadsNote>
                Shown to the customer: {row.customerVisibleMessage}
              </CustomerReadsNote>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  );
}
